/*
 * Black November collection grid.
 *
 * A separate custom element rather than changes to the theme's own
 * facet-filters.js, for two reasons: the sale needs its own markup and
 * pinned bar, and the live accessibility branch is already editing
 * facet-filters.js — sharing the file would mean a merge conflict for no
 * benefit.
 *
 * Re-renders through the Section Rendering API and keeps the URL in step,
 * so filtering never reloads the page and Back/Forward restore the state.
 * Everything degrades: with JS off, the form submits and the page filters
 * the ordinary way.
 */
if (!customElements.get('bn-collection')) {
  class BnCollection extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      // Two forms: the sidebar/bar one and the drawer one. Which one a change
      // came from decides which set of controls describes the new state.
      this.forms = Array.from(this.querySelectorAll('form'));
      this.results = this.querySelector('.js-bn-results');
      this.strip = this.querySelector('.js-bn-dept-strip');
      this.drawer = this.querySelector('.bn-drawer');
      this.openDrawerBtn = this.querySelector('.js-bn-open-drawer');
      this.openSortBtn = this.querySelector('.js-bn-open-sort');

      this.onFormChange = this.onFormChange.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onStripScroll = this.onStripScroll.bind(this);

      this.forms.forEach((form) => {
        form.addEventListener('change', this.onFormChange);
        form.bnSubmitHandler = (event) => {
          event.preventDefault();
          this.apply(this.paramsFromForm(form));
        };
        form.addEventListener('submit', form.bnSubmitHandler);
      });
      this.addEventListener('click', this.onClick);
      // On document, not on the element: a dialog's Escape has to work even
      // when focus has landed somewhere outside the component, and a keydown
      // on <body> never bubbles through here.
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);

      if (this.strip) {
        this.strip.addEventListener('scroll', this.onStripScroll, { passive: true });
        this.updateArrows();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('keydown', this.onKeydown);
      // Everything added in connectedCallback comes off again, so an element
      // that is disconnected and reconnected does not end up with two of each
      // and fire every filter change twice.
      this.removeEventListener('click', this.onClick);
      (this.forms || []).forEach((form) => {
        form.removeEventListener('change', this.onFormChange);
        if (form.bnSubmitHandler) form.removeEventListener('submit', form.bnSubmitHandler);
      });
      if (this.strip) this.strip.removeEventListener('scroll', this.onStripScroll);
      // Pending timers too, or a price-field debounce can fire against a
      // detached element and push history for a section that is gone.
      clearTimeout(this.priceTimer);
      clearTimeout(this.arrowTimer);
    }

    /* ------------------------------------------------------------ state */

    paramsFromForm(form) {
      // Start from the URL, not from an empty set. A filter group the template
      // hides (filters_show_only / filters_hide) renders no inputs, so building
      // the query purely from the form would silently drop its active value the
      // moment the shopper touched anything else — someone arriving from a
      // marketing link would watch their filter disappear. Anything this form
      // owns gets cleared and rewritten; anything it does not own is left alone.
      const params = new URLSearchParams(window.location.search);
      form.querySelectorAll('[name]').forEach((el) => params.delete(el.name));
      // Any change returns to the first page.
      params.delete('page');
      new FormData(form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });
      return params.toString();
    }

    onFormChange(event) {
      const form = event.target.form || event.currentTarget;
      // Remember what the shopper was on, so focus survives the re-render.
      this.pendingFocus = {
        name: event.target.name,
        value: event.target.value,
        type: event.target.type,
        inDrawer: !!event.target.closest('.bn-drawer'),
      };

      // Price inputs fire change on every keystroke in some browsers; let the
      // field settle before refetching so typing "1200" is one request.
      if (event.target.type === 'number') {
        clearTimeout(this.priceTimer);
        this.priceTimer = setTimeout(() => this.apply(this.paramsFromForm(form)), 500);
        return;
      }
      this.apply(this.paramsFromForm(form));
    }

    onPopState() {
      this.render(window.location.search.replace(/^\?/, ''), false);
    }

    apply(params) {
      this.render(this.keepView(params), true);
    }

    /*
     * Links built in Liquid — department buttons, applied-filter pills, Clear
     * all, pagination — describe the filters but know nothing about `view`,
     * the parameter that selects an alternate template. Dropping it sends the
     * shopper to the default collection template instead of this one, which
     * looks exactly like "the filter did nothing". Only bites while the
     * template is being previewed with ?view=, but that is when it is tested.
     */
    keepView(params) {
      const view = new URLSearchParams(window.location.search).get('view');
      if (!view) return params;
      const next = new URLSearchParams(params);
      if (!next.has('view')) next.set('view', view);
      return next.toString();
    }

    async render(params, pushState) {
      this.setBusy(true);

      const url = `${window.location.pathname}?${params}`;
      try {
        const response = await fetch(`${url}${params ? '&' : ''}section_id=${this.sectionId}`);
        if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');

        this.swap(html, '.js-bn-results');
        this.swap(html, '.bn-sidebar');
        this.swap(html, '.bn-drawer__body');
        this.swapDeptInputs(html);
        this.swapDeptStrip(html);
        this.syncBadgeAndFooter(html);
        this.syncSort(html);
        this.restoreFocus();

        if (pushState) window.history.pushState({ bn: true }, '', url);
      } catch (error) {
        // Fall back to a normal navigation rather than stranding the shopper
        // on a grid that no longer matches the controls they just used.
        console.error('[bn] falling back to full navigation', error);
        window.location.href = url;
        return;
      } finally {
        this.setBusy(false);
      }
    }

    swap(html, selector) {
      const next = html.querySelector(selector);
      const current = this.querySelector(selector);
      if (next && current) current.innerHTML = next.innerHTML;
    }

    // Both forms carry their own copy, so swap every one of them.
    swapDeptInputs(html) {
      const next = html.querySelector('.js-bn-dept-inputs');
      if (!next) return;
      this.querySelectorAll('.js-bn-dept-inputs').forEach((el) => {
        el.innerHTML = next.innerHTML;
      });
    }

    // The pinned bar is not swapped, so its select needs syncing by hand or
    // Back/Forward leaves it showing the previous choice.
    syncSort(html) {
      const next = html.querySelector('.bn-bar__sort .js-bn-sort');
      const current = this.querySelector('.bn-bar__sort .js-bn-sort');
      if (next && current) current.value = next.value;
    }

    restoreFocus() {
      const target = this.pendingFocus;
      this.pendingFocus = null;
      if (!target || !target.name) return;
      const scope = target.inDrawer ? this.drawer : this.querySelector('.bn-sidebar');
      if (!scope) return;

      let el;
      if (target.type === 'checkbox') {
        el = Array.from(scope.querySelectorAll('input[type="checkbox"]')).find(
          (input) => input.name === target.name && input.value === target.value
        );
      } else {
        el = scope.querySelector(`[name="${CSS.escape(target.name)}"]`);
      }
      if (el) el.focus();
    }

    swapDeptStrip(html) {
      const next = html.querySelector('.js-bn-dept-strip');
      if (!next || !this.strip) return;
      // Keep the shopper's scroll position in the department row; replacing it
      // wholesale would snap them back to "All deals" after every filter.
      const left = this.strip.scrollLeft;
      this.strip.innerHTML = next.innerHTML;
      this.strip.scrollLeft = left;
      this.updateArrows();
    }

    syncBadgeAndFooter(html) {
      const nextBadge = html.querySelector('.bn-bar__badge');
      const currentBadge = this.querySelector('.bn-bar__badge');
      const btn = this.openDrawerBtn;
      if (btn) {
        if (nextBadge && currentBadge) {
          currentBadge.textContent = nextBadge.textContent;
        } else if (nextBadge && !currentBadge) {
          btn.insertAdjacentHTML('beforeend', nextBadge.outerHTML);
        } else if (!nextBadge && currentBadge) {
          currentBadge.remove();
        }
      }

      const nextFoot = html.querySelector('.bn-drawer__foot');
      const currentFoot = this.querySelector('.bn-drawer__foot');
      if (nextFoot && currentFoot) currentFoot.innerHTML = nextFoot.innerHTML;
    }

    setBusy(busy) {
      if (this.results) this.results.setAttribute('aria-busy', busy ? 'true' : 'false');
      this.classList.toggle('is-loading', busy);  // styled as bn-collection.is-loading
    }

    /* ----------------------------------------------------------- events */

    onClick(event) {
      const dept = event.target.closest('.bn-deptbtn');
      if (dept) {
        const deptHref = dept.getAttribute('href');
        if (!deptHref) return;
        event.preventDefault();
        this.apply(deptHref.split('?')[1] || '');
        this.scrollToTop();
        return;
      }

      if (event.target.closest('.js-bn-open-drawer')) return this.openDrawer();
      if (event.target.closest('.js-bn-open-sort')) return this.openDrawer('.js-bn-sort');
      if (event.target.closest('.js-bn-close-drawer')) return this.closeDrawer();

      if (event.target.closest('.js-bn-scroll-left')) return this.scrollStrip(-480);
      if (event.target.closest('.js-bn-scroll-right')) return this.scrollStrip(480);

      const showMore = event.target.closest('.js-bn-show-more');
      if (showMore) {
        const group = showMore.closest('.bn-filter');
        if (group) group.classList.add('is-expanded');
        showMore.remove();
        return;
      }

      // Applied-filter pills, "Clear all" and pagination are plain links.
      // .js-bn-clear covers the drawer footer and empty-state "Clear all",
      // which otherwise hard-navigate and lose the ?view= template param.
      const link = event.target.closest(
        '.bn-applied__pill, .bn-applied__clear, .js-bn-clear, .bn-pagination a'
      );
      if (link) {
        // The theme renders disabled prev/next arrows as <a> with no href.
        const href = link.getAttribute('href');
        if (!href) return;
        event.preventDefault();
        if (link.closest('.bn-drawer')) this.closeDrawer();
        this.apply(href.split('?')[1] || '');
        this.scrollToTop();
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) {
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !this.drawer || !this.drawer.classList.contains('is-open')) return;

      const focusable = this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select, summary, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /* ----------------------------------------------------------- drawer */

    openDrawer(focusSelector) {
      if (!this.drawer) return;
      // In Safari a mouse click can leave activeElement as <body>; fall back to
      // the button so Escape returns focus somewhere useful.
      const active = document.activeElement;
      this.lastFocused = active && active !== document.body ? active : this.openDrawerBtn;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.setDrawerExpanded(true);
      document.body.style.overflow = 'hidden';
      const focusTarget = focusSelector ? this.drawer.querySelector(focusSelector) : null;
      (focusTarget || this.drawer).focus();
    }

    setDrawerExpanded(open) {
      [this.openDrawerBtn, this.openSortBtn].forEach((btn) => {
        if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.setDrawerExpanded(false);
      document.body.style.overflow = '';
      // Focus goes back to the button that opened it, not to the top of the page.
      if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
    }

    /* ------------------------------------------------------- dept strip */

    scrollStrip(amount) {
      if (!this.strip) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.strip.scrollBy({ left: amount, behavior: reduce ? 'auto' : 'smooth' });
    }

    onStripScroll() {
      clearTimeout(this.arrowTimer);
      this.arrowTimer = setTimeout(() => this.updateArrows(), 100);
    }

    updateArrows() {
      if (!this.strip) return;
      const left = this.querySelector('.js-bn-scroll-left');
      const right = this.querySelector('.js-bn-scroll-right');
      const max = this.strip.scrollWidth - this.strip.clientWidth;
      // A 2px tolerance: fractional scroll widths otherwise leave an arrow
      // enabled at the end of the row with nothing left to scroll to.
      if (left) left.disabled = this.strip.scrollLeft <= 2;
      if (right) right.disabled = this.strip.scrollLeft >= max - 2;
    }

    scrollToTop() {
      const anchor = document.getElementById('all-deals');
      if (!anchor) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      anchor.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  }

  customElements.define('bn-collection', BnCollection);
}
